/* Pure personal-binder state and search rules. Shared with the offline tests. */
((scope) => {
  'use strict';
  const MAX_BINDERS = 10;
  const MAX_SLOTS = 1350;
  const PRINTING = /^card:(\d{1,9}):(\d{1,12}):([a-z0-9-]{1,80})$/;
  const clone = value => JSON.parse(JSON.stringify(value));
  const equal = (left, right) => JSON.stringify(left) === JSON.stringify(right);
  const empty = () => ({schema: 1, binders: {}, owned: {}, wishes: {}});
  const validKey = key => typeof key === 'string' && PRINTING.test(key);
  const list = value => Array.isArray(value) ? value : [];
  const cleanName = value => String(value || '').trim().slice(0, 100);
  function id() {
    const bytes = new Uint8Array(12);
    scope.crypto.getRandomValues(bytes);
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  }
  function normalizeBinder(input) {
    if (!input || !['pokedex', 'custom'].includes(input.kind) || !/^[a-f0-9]{24}$/.test(input.uid || '')) return null;
    if (Array.isArray(input.entries)) {
      const unpacked = {...input, order: [], slots: {}};
      for (const entry of input.entries.slice(0, MAX_SLOTS)) {
        if (!Array.isArray(entry)) continue;
        const [slot, choice, rarities, query, set, finish] = entry;
        unpacked.order.push(slot);
        unpacked.slots[slot] = {choice, rarities: rarities === undefined ? null : rarities, query, set, finish,
          species: input.kind === 'pokedex' ? Number(String(slot).slice(1)) : 0};
      }
      input = unpacked;
    }
    const binder = {uid: input.uid, name: cleanName(input.name) || 'My binder', kind: input.kind,
      rarities: list(input.rarities).filter(v => typeof v === 'string').slice(0, 40), slots: {}, order: []};
    for (const slotId of list(input.order).slice(0, MAX_SLOTS)) {
      if (!/^s[a-z0-9_-]{1,40}$/.test(slotId) || binder.order.includes(slotId)) continue;
      const source = input.slots?.[slotId];
      if (!source || typeof source !== 'object') continue;
      const species = Number(source.species) || 0;
      binder.slots[slotId] = {choice: validKey(source.choice) ? source.choice : null, species,
        rarities: source.rarities === null || source.rarities === undefined ? null : list(source.rarities).filter(v => typeof v === 'string').slice(0, 40),
        query: String(source.query || '').slice(0, 400), set: Number(source.set) || 0, finish: String(source.finish || '').slice(0, 80)};
      binder.order.push(slotId);
    }
    return binder;
  }
  function normalize(input) {
    const state = empty();
    if (!input || input.schema !== 1) return state;
    for (let index = 0; index < MAX_BINDERS; index++) {
      const key = 'b' + index;
      const binder = normalizeBinder(input.binders?.[key]);
      if (binder) state.binders[key] = binder;
    }
    for (const [key, owned] of Object.entries(input.owned || {})) {
      if (validKey(key) && owned === true) state.owned[key] = true;
    }
    for (const [key, wish] of Object.entries(input.wishes || {}).slice(0, 500)) {
      if (/^(card|product|search):/.test(key) && key.length <= 350 && wish && typeof wish === 'object') state.wishes[key] = clone(wish);
    }
    return state;
  }
  function create(state, kind, name, catalog, generation = 0) {
    const key = Array.from({length: MAX_BINDERS}, (_, i) => 'b' + i).find(k => !state.binders[k]);
    if (!key) throw Error('You can save up to 10 binders. Remove a binder before creating another.');
    const binder = {uid: id(), kind, name: cleanName(name) || (kind === 'pokedex' ? (generation ? 'Generation ' + generation + ' Pokédex' : 'National Pokédex') : 'My binder'), rarities: [], slots: {}, order: []};
    if (kind === 'pokedex') {
      const speciesList = catalog.species.filter(species => !generation || species.generation === Number(generation));
      if (!speciesList.length) throw Error('Choose an available Pokédex generation.');
      for (const species of speciesList) {
        const slot = 's' + species.id;
        binder.order.push(slot);
        binder.slots[slot] = {species: species.id, choice: catalog.starters[String(species.id)]?.key || null, rarities: null};
      }
    } else addPage(binder);
    state.binders[key] = binder;
    return key;
  }
  function addPage(binder) {
    if (binder.kind !== 'custom') throw Error('Pokédex pages follow the Pokémon list.');
    if (binder.order.length + 9 > MAX_SLOTS) throw Error('This binder has reached its 150-page limit.');
    const prefix = id().slice(0, 12);
    for (let i = 0; i < 9; i++) {
      const slot = 's' + prefix + i;
      binder.order.push(slot);
      binder.slots[slot] = {choice: null, species: 0, rarities: null};
    }
  }
  function wanted(state, binder) {
    const result = new Set();
    for (const book of binder ? [binder] : Object.values(state.binders)) {
      for (const slotId of book.order) {
        const key = book.slots[slotId]?.choice;
        if (key && !state.owned[key]) result.add(key);
      }
    }
    return [...result];
  }
  function merge(base, mine, theirs, path = []) {
    if (equal(mine, base)) return {value: cloneOrMissing(theirs), conflicts: []};
    if (equal(theirs, base) || equal(mine, theirs)) return {value: cloneOrMissing(mine), conflicts: []};
    const objects = [base, mine, theirs].every(v => v && typeof v === 'object' && !Array.isArray(v));
    if (objects && !(mine.uid && theirs.uid && mine.uid !== theirs.uid)) {
      const value = {}, conflicts = [];
      for (const key of new Set([...Object.keys(base), ...Object.keys(mine), ...Object.keys(theirs)])) {
        const child = merge(base[key], mine[key], theirs[key], [...path, key]);
        if (child.value !== undefined) value[key] = child.value;
        conflicts.push(...child.conflicts);
      }
      return {value, conflicts};
    }
    return {value: cloneOrMissing(mine), conflicts: [path.join('.')]};
  }
  function cloneOrMissing(value) { return value === undefined ? undefined : clone(value); }
  function bucket(key, count) {
    let value = 0;
    for (let i = 0; i < key.length; i++) value = (value * 31 + key.charCodeAt(i)) >>> 0;
    return value % count;
  }
  function documents(state) {
    const result = {library: {schema: 1, binders: Object.keys(state.binders)}};
    for (let i = 0; i < MAX_BINDERS; i++) {
      const binder = state.binders['b' + i];
      result['binder_' + i] = binder ? {uid: binder.uid, name: binder.name, kind: binder.kind, rarities: binder.rarities,
        entries: binder.order.map(id => {
          const slot = binder.slots[id];
          const entry = [id, slot.choice, slot.rarities, slot.query || '', slot.set || 0, slot.finish || ''];
          while (entry.length > 2 && (entry[entry.length - 1] === '' || entry[entry.length - 1] === 0 || entry[entry.length - 1] === null)) entry.pop();
          return entry;
        })} : null;
    }
    for (let i = 0; i < 32; i++) result['owned_' + i] = {};
    for (let i = 0; i < 8; i++) result['wishes_' + i] = {};
    for (const [key, value] of Object.entries(state.owned)) result['owned_' + bucket(key, 32)][key] = value;
    for (const [key, value] of Object.entries(state.wishes)) result['wishes_' + bucket(key, 8)][key] = value;
    for (const value of Object.values(result)) {
      if (new TextEncoder().encode(JSON.stringify(value)).length > 120000) throw Error('This binder is too large to sync. Split it into smaller binders.');
    }
    return result;
  }
  function fromDocuments(docs) {
    const state = empty();
    for (let i = 0; i < MAX_BINDERS; i++) if (docs['binder_' + i]) state.binders['b' + i] = docs['binder_' + i];
    for (let i = 0; i < 32; i++) Object.assign(state.owned, docs['owned_' + i] || {});
    for (let i = 0; i < 8; i++) Object.assign(state.wishes, docs['wishes_' + i] || {});
    return normalize(state);
  }
  function search(text) {
    const tokens = [];
    let position = 0, cursor = 0;
    while (position < text.length) {
      if (/\s/.test(text[position])) { position++; continue; }
      const char = text[position];
      if ('():-'.includes(char)) { tokens.push({kind: char}); position++; continue; }
      let value = '';
      if (char === '"' || char === "'") {
        position++;
        while (position < text.length && text[position] !== char) {
          if (text[position] === '\\') position++;
          value += text[position++] || '';
        }
        if (text[position++] !== char) throw Error('Close the quotation marks.');
        tokens.push({kind: 'text', value});
      } else {
        while (position < text.length && !/[\s():]/.test(text[position])) {
          if (text[position] === '\\') position++;
          value += text[position++] || '';
        }
        tokens.push({kind: ['AND', 'OR', 'NOT'].includes(value) ? value : 'text', value});
      }
    }
    const fields = ['title', 'name', 'tag', 'set', 'number', 'rarity', 'finish', 'artist'];
    function term() {
      const token = tokens[cursor++];
      if (!token) throw Error('Add a search term after the operator.');
      if (token.kind === 'NOT' || token.kind === '-') {
        const child = term(); return card => !child(card);
      }
      if (token.kind === '(') {
        const child = andGroup();
        if (tokens[cursor++]?.kind !== ')') throw Error('Close the parentheses.');
        return child;
      }
      if (token.kind !== 'text') throw Error('Check the search operators.');
      let field = '', value = token.value;
      if (tokens[cursor]?.kind === ':') {
        field = value.toLowerCase(); cursor++;
        if (!fields.includes(field)) throw Error('Use title, set, number, rarity, finish, artist or tag as a search field.');
        const next = tokens[cursor++];
        if (next?.kind !== 'text') throw Error('Add a value after the search field.');
        value = next.value;
      }
      const pieces = value.toLocaleLowerCase().split('*').map(part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      const pattern = new RegExp(pieces.join('.*'), 'i');
      return card => {
        const values = {title: card.name, name: card.name, set: card.setName, number: card.number,
          rarity: card.rarity, finish: card.finish, artist: card.artist,
          tag: ['rarity: ' + card.rarity, 'artist: ' + card.artist, 'set: ' + card.setName, 'finish: ' + card.finish].join(' ')};
        return pattern.test(field ? values[field] || '' : Object.values(values).join(' '));
      };
    }
    // Shopify gives OR higher precedence than AND. Parentheses remain explicit.
    function orGroup() {
      let left = term();
      while (tokens[cursor]?.kind === 'OR') { cursor++; const a = left, b = term(); left = card => a(card) || b(card); }
      return left;
    }
    function andGroup() {
      let left = orGroup();
      while (cursor < tokens.length && tokens[cursor].kind !== ')') {
        if (tokens[cursor].kind === 'AND') cursor++;
        const a = left, b = orGroup(); left = card => a(card) && b(card);
      }
      return left;
    }
    if (!tokens.length) return () => true;
    const match = andGroup();
    if (cursor !== tokens.length) throw Error('Remove the extra closing parenthesis.');
    return match;
  }
  const api = {MAX_BINDERS, MAX_SLOTS, clone, equal, empty, normalize, normalizeBinder,
    create, addPage, wanted, merge, documents, fromDocuments, search, validKey, id};
  scope.KillinBinderModel = api;
  if (typeof module !== 'undefined') module.exports = api;
})(typeof window === 'undefined' ? globalThis : window);
